import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack } from '@mui/material';
import { Formik } from 'formik';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import PublishIcon from '@mui/icons-material/Publish';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';

import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs, CardTitle } from 'src/core/ui/ContentCard';
import { FormField } from 'src/core/forms/FormField';
import CustomButton from 'src/core/ui/CustomButton';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import SelectTableDialog from 'src/core/tables/SelectTableDialog';

import FormHelper from 'src/core/forms/formHelper';
import { commitPendingTableEdits } from 'src/core/tables/helpers/tableEditActions';
import { getRowsFromTableSettings } from 'src/core/tables/tableHelper';
import { CustomField } from 'src/core/forms/CustomField';

import { useCampaignForm } from 'src/core/forms/useForms';
import { useCampaignOptions } from 'src/core/forms/useOptions';
import { useFormDefaults } from 'src/core/forms/useFormDefaults';
import { FormDefaultsMenu } from 'src/core/forms/FormDefaultsMenu';

import useCampaignSubmit from 'src/pages/orders/campaigns/useCampaignSubmit';
import { useCampaignSelectorTables, type CampaignBulkPriceEdit, type CampaignBulkPriceEditKind } from 'src/pages/orders/campaigns/useSelectItemsOnCampaign';
import useRedirectOnCreate from 'src/core/forms/useRedirectOnCreate';

export const CampaignFormPage = () => {
  const [editId, PageName, , , isEditPage] = FormHelper.getEditPageConfig('Campaign');
  const initialValues: any = useCampaignForm(editId, defaultInitialValues);
  const [results, submit, closeCampaignSubmit, publishCampaignSubmit, submitting] = useCampaignSubmit(isEditPage, null);
  const [campaignTypeOptions, , , discountTypeOptions, rawSalesPriceLists] = useCampaignOptions();
  const [salesItemDisplayColumns, salesItemDisplayTable, priceListDisplayColumns, priceListDisplayTable, campaignPricesColumns, campaignPricesTable, campaignTableData, campaignPriceEditModes, campaignPriceBulkEditDialog, salesItemSelector, priceListSelector] = useCampaignSelectorTables(initialValues, rawSalesPriceLists ?? [], discountTypeOptions ?? []);
  const campaignSubmitData = useMemo(() => ({ ...campaignTableData, campaignPriceEditModes }), [campaignTableData, campaignPriceEditModes]);
  const { FormDefaultsListener, formDefaultsProps } = useFormDefaults({ formKey: 'EditCampaignForm', isEditPage, fields: { CampaignType: {}, ValidFrom: {}, ValidTo: {} } });
  const hasSalesPriceLists = (initialValues?.SalesPriceLists?.length ?? 0) > 0;
  const hasSalesItems = (initialValues?.CampaignSalesItems?.length ?? 0) > 0;
  const hasPrices = (initialValues?.CampaignPrices?.length ?? 0) > 0;
  const hasRule = initialValues?.CampaignSalesRule != null && initialValues.CampaignSalesRule !== '';
  const tabs: CardTab[] = useMemo<CardTab[]>(
    () => [
      { value: 'Campaign Prices', label: 'Campaign Prices' },
      { value: 'Campaign Sales Items', label: 'Campaign Sales Items' },
      { value: 'Sales Price Lists', label: 'Sales Price Lists' },
      { value: 'Campaign Rule', label: 'Campaign Rule' }
    ],
    []
  );
  const computeDefaultTab = () => {
    if (hasPrices) return 'Campaign Prices';
    if (hasSalesItems) return 'Campaign Sales Items';
    if (hasSalesPriceLists) return 'Sales Price Lists';
    if (hasRule) return 'Campaign Rule';
    return tabs[0].value;
  };
  const [currentTab, setCurrentTab] = useState<string>();
  useEffect(() => {
    setCurrentTab((prev) => {
      const defaultTab = computeDefaultTab();
      if (!prev) return defaultTab;
      const stillExists = tabs.some((t) => t.value === prev);
      return stillExists ? prev : defaultTab;
    });
  }, [hasSalesItems, hasPrices, hasSalesPriceLists, hasRule, tabs]);
  useRedirectOnCreate(results?.mainResult, editId, 'Id', '/orders/campaigns/edit?id=');
  return (
    <>
      <Formik initialValues={initialValues} validateOnBlur={true} validateOnChange={false} validationSchema={scheme} onSubmit={async (values: any, formikHelpers: any) => {
          await commitPendingTableEdits(campaignPricesTable);
          submit(values, { ...campaignSubmitData, campaignPrices: getRowsFromTableSettings(campaignPricesTable) }, formikHelpers);
        }}
        enableReinitialize
      >
        {(props): JSX.Element => {
          const { selectorEnabled, selectorTooltipText } = getCampaignSelectorState(props.values?.Status_Value);
          const campaignStatus = String(props.values?.Status_Value ?? initialValues?.Status_Value ?? '').trim().toLowerCase();
          const isDraftCampaign = campaignStatus === 'draft';
          const isClosedCampaign = campaignStatus === 'closed';

          return (
            <div>
              <FormDefaultsListener formikProps={props} />
              {selectorEnabled && <SelectTableDialog {...salesItemSelector} />}
              {selectorEnabled && <SelectTableDialog {...priceListSelector} />}
              <CampaignPriceBulkEditDialog {...campaignPriceBulkEditDialog} />
              <PageHeader title={PageName} initialValues={initialValues} subTitle={'Edit and manage Campaign details'} components={[
                  { component: (<CustomButton disabled={!isEditPage || !isDraftCampaign} tooltipText={ !isEditPage ? 'Campaign must be created before it can be published.' : !isDraftCampaign ? 'Only campaigns with Draft status can be published.' : undefined } isSubmitting={submitting} onClick={() => { publishCampaignSubmit(String(editId), props); }} title="Publish" icon={<PublishIcon />} /> ) },
                  { component: (<CustomButton disabled={!isEditPage || isClosedCampaign} tooltipText={!isEditPage ? 'Campaign must be created before it can be closed.' : isClosedCampaign ? 'Campaign is already closed.' : undefined} isSubmitting={submitting} onClick={() => { closeCampaignSubmit(String(editId), props); }} title="Close" icon={<LockIcon />} /> ) },
                  { isInGroupedMenu: true, component: <FormDefaultsMenu formikProps={props} formDefaultsProps={formDefaultsProps} /> },
                  { isInGroupedMenu: false, component: <CustomButton isSubmitting={submitting} onClick={props.handleSubmit} title="Save" icon={<SaveIcon />} /> }
                ]}
              />
              <ContentCardsContainer>
                <ContentCard {...{ cardId: 'Campaign Info', md: 12, formLayout: true, cardHeader: <CardTitle icon={<CampaignOutlinedIcon />}>Campaign Info</CardTitle> }}>
                  <FormField fieldProps={{ name: 'Name', label: 'Name', md: 12 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'Status_Value', label: 'Status', md: 6, disabled: true }} formikProps={props} />
                  <FormField fieldProps={{ name: 'CampaignType', select: true, label: 'Campaign Type', md: 6, tooltipText: 'Select what kind of campaign this is, for example price reduction, free shipping or order discount.' }} formikProps={props} >
                    {campaignTypeOptions?.map((option: any) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>
                  <FormField fieldProps={{ name: 'ValidFrom', label: 'Valid From', type: 'date', md: 4 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'ValidTo', label: 'Valid To', type: 'date', md: 4 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'SalesText', label: 'Sales Text', multiLine: true, rows: 3, md: 12 }} formikProps={props} />
                </ContentCard>
              </ContentCardsContainer>
              <ContentCardsContainer>
                <ContentCard cardId={'TabContainer'} contentPadding={0} cardTypes={[CardType.tabContainer]} cardHeader={<HeaderTabs {...{ tabs, currentTab: currentTab ?? computeDefaultTab(), setCurrentTab }} />} components={[
                    { component: (<CustomButton onClick={() => salesItemSelector.openSelector()} title="Add Sales Item" icon={<AddTwoToneIcon />} disabled={!selectorEnabled} tooltipText={selectorTooltipText} /> ) },
                    { component: (<CustomButton onClick={() => priceListSelector.openSelector()} title="Add Price List" icon={<AddTwoToneIcon />} disabled={!selectorEnabled} tooltipText={selectorTooltipText} /> ) }
                  ]}
                >
                  {(currentTab ?? computeDefaultTab()) === 'Campaign Prices' && (
                    <ContentCard {...{ cardId: 'Campaign Prices', cardTypes: [CardType.tab], contentPadding: 0 }}>
                      <CustomDataGrid columns={campaignPricesColumns} tableSettings={campaignPricesTable} />
                    </ContentCard>
                  )}
                  {(currentTab ?? computeDefaultTab()) === 'Campaign Sales Items' && (
                    <ContentCard {...{ cardId: 'Campaign Sales Items', cardTypes: [CardType.tab], contentPadding: 0 }}>
                      <CustomDataGrid columns={salesItemDisplayColumns} tableSettings={salesItemDisplayTable} />
                    </ContentCard>
                  )}
                  {(currentTab ?? computeDefaultTab()) === 'Sales Price Lists' && (
                    <ContentCard {...{ cardId: 'Sales Price Lists', cardTypes: [CardType.tab], contentPadding: 0 }}>
                      <CustomDataGrid columns={priceListDisplayColumns} tableSettings={priceListDisplayTable} />
                    </ContentCard>
                  )}
                  {(currentTab ?? computeDefaultTab()) === 'Campaign Rule' && (
                    <ContentCard {...{ cardId: 'Campaign Rule', cardTypes: [CardType.tab], contentPadding: 2, formLayout: true, cardHeader: <CardTitle icon={<RuleOutlinedIcon />}>Campaign Rule</CardTitle> }} >
                      {hasRule ? (
                        <>
                          <FormField fieldProps={{ name: 'CampaignSalesRule', label: 'Rule ID', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_CampaignId', label: 'Campaign ID', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_Prio', label: 'Priority', type: 'number', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_OrderDiscount', label: 'Order Discount', type: 'number', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_OrderDiscountType_Value', label: 'Discount Type', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_MinimumAmountGross', label: 'Min Order Amount (gross)', type: 'number', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_MinimumQuantity', label: 'Min Quantity', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_FreeArticleSalesItemId', label: 'Free Article ID', type: 'number', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_FreeQuantity', label: 'Free Quantity', type: 'number', md: 4, disabled: true }} formikProps={props} />
                          <FormField fieldProps={{ name: 'CampaignSalesRule_FreeArticleSalesItemName', label: 'Free Article Name', md: 8, disabled: true }} formikProps={props} />
                        </>
                      ) : (
                        <div style={{ padding: 16 }}>No campaign rule defined.</div>
                      )}
                    </ContentCard>
                  )}
                </ContentCard>
              </ContentCardsContainer>
            </div>
          );
        }}
      </Formik>
      <Footer />
    </>
  );
};

const scheme = Yup.object().shape({ Id: Yup.number().nullable(), Name: Yup.string().required('Name is required'), SalesText: Yup.string().nullable(), CampaignType: Yup.number().typeError('Select a campaign type so the campaign is handled correctly.').required('Select a campaign type so the campaign is handled correctly.'), Status: Yup.object({ Key: Yup.number().nullable(), Value: Yup.string().nullable() }).nullable(), ValidFrom: Yup.date().nullable(), ValidTo: Yup.date().nullable().min(Yup.ref('ValidFrom'), 'Valid To must be after Valid From'), SalesPriceLists: Yup.array().nullable(), CampaignSalesItems: Yup.array().nullable(), CampaignPrices: Yup.array().nullable(), CampaignSalesRule: Yup.mixed().nullable() });

const defaultInitialValues = { Status_Value: '', CampaignType: '' };

const parseNumLoose = (value: any) => {
  if (value == null) return NaN;
  const cleaned = String(value).replace(',', '.').trim();
  if (cleaned === '') return NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const stripLeadingZeroIfMultiDigit = (value: string) => (/^0\d+$/.test(value) ? String(Number(value)) : value);

function CampaignPriceBulkEditDialog({ open, selectedCount, onClose, onApply }: { open: boolean; selectedCount: number; onClose: () => void; onApply: (edit: CampaignBulkPriceEdit) => void; }) {
  const [activeField, setActiveField] = useState<CampaignBulkPriceEditKind>('fixedPrice');
  const [fixedPriceInput, setFixedPriceInput] = useState('');
  const [discountAmountInput, setDiscountAmountInput] = useState('');
  const [discountRateInput, setDiscountRateInput] = useState('');
  useEffect(() => {
    if (!open) return;
    setActiveField('fixedPrice');
    setFixedPriceInput('');
    setDiscountAmountInput('');
    setDiscountRateInput('');
  }, [open]);
  const activeEdit = useMemo<CampaignBulkPriceEdit | null>(() => {
    const value =
      activeField === 'fixedPrice' ? parseNumLoose(fixedPriceInput) : activeField === 'discountAmount' ? parseNumLoose(discountAmountInput) : parseNumLoose(discountRateInput);
    return Number.isFinite(value) ? { kind: activeField, value } : null;
  }, [activeField, fixedPriceInput, discountAmountInput, discountRateInput]);
  const setExclusiveValue = (kind: CampaignBulkPriceEditKind, value: string) => {
    const nextInput = stripLeadingZeroIfMultiDigit(value ?? '');
    setActiveField(kind);
    setFixedPriceInput(kind === 'fixedPrice' ? nextInput : '');
    setDiscountAmountInput(kind === 'discountAmount' ? nextInput : '');
    setDiscountRateInput(kind === 'discountRate' ? nextInput : '');
  };
  const handleApply = () => {
    if (!activeEdit) return;
    onApply(activeEdit);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk edit campaign prices</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <CustomField options={{ name: 'fixedPrice', label: 'Fixed price', topMargin: '0', handleChange: (event) => setExclusiveValue('fixedPrice', event.target.value) }} stateProps={{ state: fixedPriceInput }} />
            <CustomField options={{ name: 'discountAmount', label: 'Discount amount', topMargin: '0', handleChange: (event) => setExclusiveValue('discountAmount', event.target.value) }} stateProps={{ state: discountAmountInput }} />
            <CustomField options={{ name: 'discountRate', label: 'Discount rate (%)', topMargin: '0', handleChange: (event) => setExclusiveValue('discountRate', event.target.value) }} stateProps={{ state: discountRateInput }} />
          </Stack>
          <span>{selectedCount} selected campaign price rows will be updated.</span>
        </Stack>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={onClose} title="Cancel" variant="text" />
        <CustomButton onClick={handleApply} disabled={!activeEdit} title="Apply" />
      </DialogActions>
    </Dialog>
  );
}

const getCampaignSelectorState = (statusValue: any) => {
  const normalizedStatus = String(statusValue ?? '').trim().toLowerCase();
  const selectorEnabled = normalizedStatus !== 'closed';
  return { selectorEnabled, selectorTooltipText: selectorEnabled ? '' : 'Sales items and price lists cannot be changed when the campaign is Closed.' };
};

export default CampaignFormPage;
